import { IconCheck, IconEdit, IconPencil, IconWand } from "@tabler/icons-react";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  useMantineReactTable
} from "mantine-react-table-open";
import { useMemo, useState } from "react";
import { generatePath, Link } from "react-router";
import { Userpilot } from "userpilot";
import { DisplayStatus } from "../../../components/MsmProjectView/Sites/SetupProcessItem/DisplayStatus";
import { IconButton } from "../../../mantine/components/IconButton/IconButton";
import { getMinimalMRTOptions } from "../../../mantine/getMinimalMRTOptions";
import { ROUTES } from "../../../routes";
import { SiteSetupProcessStatus } from "../../../types/api.types";
import { useStartPageSiteSetupProcesses } from "../hooks/useStartPageSiteSetupProcesses";
import type { SiteSetupProcessTileData } from "../StartPage.types";
import "./SiteSetupProcessesTile.scss";
import { Tile } from "./Tile";

interface SiteSetupProcessesTileProps {
  projectId?: string;
}

function SiteSetupProcessesTile({ projectId }: SiteSetupProcessesTileProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const {
    isLoading,
    error,
    data: siteSetupProcesses
  } = useStartPageSiteSetupProcesses(currentPage, pageSize, projectId);

  function handleSwitchPage(newCurrentPage: number, newPageSize: number) {
    setCurrentPage(newCurrentPage);
    setPageSize(newPageSize);
  }

  const mrtColumns: Array<MRT_ColumnDef<SiteSetupProcessTileData>> = useMemo(
    () => [
      {
        header: "Projektname",
        accessorKey: "project_name",
        Cell: ({ row }) => (
          <Link to={`../projekte/${row.original.project_id}/`}>
            {row.original.project_name}
          </Link>
        )
      },
      { header: "Liegenschaftsname", accessorKey: "site_name" },
      {
        header: "Status",
        accessorKey: "status",
        grow: false,
        Cell: ({ row }) => (
          <DisplayStatus size="xs" status={row.original.status} />
        )
      },
      {
        header: "Aktion",
        accessorKey: "id",
        grow: false,
        Cell: ({ row }) => {
          const status = row.original.status;
          const path = generatePath(ROUTES.project.siteSetupAssistant, {
            projectId: row.original.project_id,
            siteSetupProcessId: row.original.id
          });

          const label =
            status === SiteSetupProcessStatus.NotConfigured
              ? "Daten erfassen"
              : status === SiteSetupProcessStatus.InProgress
                ? "Fortschritt bearbeiten"
                : "";
          const iconName =
            status === SiteSetupProcessStatus.NotConfigured
              ? IconPencil
              : status === SiteSetupProcessStatus.InProgress
                ? IconEdit
                : IconCheck;

          const userPilotId =
            status === SiteSetupProcessStatus.NotConfigured
              ? "Startseite-Likokachel-Button-DatenErfassen"
              : "Startseite-Likokachel-Button-FortschrittBearbeiten";

          const handleButtonClick = () => {
            if (userPilotId) {
              Userpilot.track(userPilotId);
            }
          };

          return (
            <IconButton
              color="primary"
              component={Link}
              icon={iconName}
              id={userPilotId}
              size="xs"
              td="none"
              to={path}
              variant="outline"
              onClick={handleButtonClick}
            >
              {label}
            </IconButton>
          );
        }
      }
    ],
    []
  );

  const mrtTable = useMantineReactTable<SiteSetupProcessTileData>({
    ...getMinimalMRTOptions({
      emptyRowsFallbackText: "Super, alles erledigt!"
    }),
    state: {
      isLoading: isLoading,
      pagination: { pageIndex: currentPage - 1, pageSize: pageSize }
    },
    mantineTableHeadRowProps: { display: "none" },
    layoutMode: "grid",
    enableBottomToolbar: true,
    enablePagination: true,
    manualPagination: true,
    paginationDisplayMode: "pages",
    autoResetPageIndex: false,
    onPaginationChange: (pagination) => {
      if (typeof pagination === "function") {
        const currentState = { pageIndex: currentPage - 1, pageSize };
        const newState = pagination(currentState);
        handleSwitchPage(newState.pageIndex + 1, newState.pageSize);
      }
    },
    mantinePaginationProps: { size: "xs" },
    rowCount: siteSetupProcesses ? siteSetupProcesses.count : 0,
    columns: mrtColumns,
    data: siteSetupProcesses ? siteSetupProcesses.results : []
  });

  return (
    <Tile error={error} icon={IconWand} title="Liegenschaft(en) konfigurieren">
      <MantineReactTable table={mrtTable} />
    </Tile>
  );
}

export { SiteSetupProcessesTile, SiteSetupProcessesTileProps };
